import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Top } from '@/components/Top';
import { ADAPTERS, FALLBACK_MODELS, KEY_PLACEHOLDER, pickDefaultModel, PROVIDER_LABEL, PROVIDER_ORDER } from '@/llm';
import { describeError } from '@/llm/types';
import { settingsStore, useSettings } from '@/settings/settingsStore';
import { appStore, useAppState } from '@/state/appStore';
import type { Provider } from '@/types';

function ProviderCard({ p }: { p: Provider }) {
  const s = useSettings();
  const cfg = s.llm.providers[p];
  const [key, setKey] = useState(cfg?.apiKey ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const active = s.llm.activeProvider === p;
  const models = cfg?.models?.length ? cfg.models : FALLBACK_MODELS[p];

  const verify = async () => {
    const k = key.trim();
    if (!k) return;
    setBusy(true);
    setMsg(null);
    try {
      const list = await ADAPTERS[p].listModels(k);
      settingsStore.updateProvider(p, { apiKey: k, models: list, modelsFetchedAt: Date.now(), modelsSource: 'live', model: pickDefaultModel(list, cfg?.model) });
      settingsStore.setActiveProvider(p);
      setMsg(`可用，${list.length} 个模型。`);
    } catch (e) {
      settingsStore.updateProvider(p, { apiKey: k, models: FALLBACK_MODELS[p], modelsSource: 'fallback', model: cfg?.model || FALLBACK_MODELS[p][0] });
      setMsg(`没能取到模型列表：${describeError(e)}。先用内置列表。`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="rowline" style={{ justifyContent: 'space-between' }}>
        <strong>{PROVIDER_LABEL[p]}</strong>
        <span className="rowline">
          {cfg?.apiKey ? <span className="badge ok">已配置</span> : <span className="badge">未配置</span>}
          {active && cfg?.apiKey && <span className="badge ok">在用</span>}
          {!active && cfg?.apiKey && <button type="button" className="btn" onClick={() => settingsStore.setActiveProvider(p)}>用这个</button>}
        </span>
      </div>
      <div className="rowline">
        <input className="input" style={{ flex: 1, minWidth: 200 }} type="password" autoComplete="off" placeholder={KEY_PLACEHOLDER[p]} value={key} onChange={(e) => setKey(e.target.value)} />
        <button type="button" className="btn primary" disabled={!key.trim() || busy} onClick={() => void verify()}>{busy ? '检查中…' : '保存并检查'}</button>
        {cfg?.apiKey && <button type="button" className="btn danger" onClick={() => { settingsStore.clearProvider(p); setKey(''); setMsg(null); }}>清除</button>}
      </div>
      {cfg?.apiKey && (
        <div className="field">
          <label>模型</label>
          <select className="input" value={cfg.model} onChange={(e) => settingsStore.updateProvider(p, { model: e.target.value })}>
            {models.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      )}
      {msg && <p className="hint">{msg}</p>}
    </div>
  );
}

export function SettingsView() {
  const s = useSettings();
  const app = useAppState();
  const navigate = useNavigate();
  const [companion, setCompanion] = useState(s.companionName);
  const [me, setMe] = useState(s.userName);

  const exportAll = () => {
    const blob = new Blob([JSON.stringify(appStore.exportData(), null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `puppy-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const importAll = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      try {
        await appStore.importData(JSON.parse(await f.text()));
        appStore.notify('导入完成。');
      } catch (e) {
        appStore.notify(`导入失败：${(e as Error).message}`);
      }
    };
    input.click();
  };

  return (
    <div className="shell">
      <Top sub="设置" />
      <div className="page">
        <section>
          <h2>模型</h2>
          <p className="hint">三家都可以配，「在用」的那家负责说话；记笔记用同一家里最轻的模型。Key 只存在这台浏览器里，请求由浏览器直接发给提供商。切换提供商就是切换实验对象。</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {PROVIDER_ORDER.map((p) => <ProviderCard key={p} p={p} />)}
          </div>
        </section>

        <section>
          <h2>称呼</h2>
          <div className="card">
            <div className="rowline">
              <div className="field" style={{ flex: 1 }}><label>它叫</label><input className="input" value={companion} onChange={(e) => setCompanion(e.target.value)} /></div>
              <div className="field" style={{ flex: 1 }}><label>它叫你</label><input className="input" value={me} onChange={(e) => setMe(e.target.value)} /></div>
            </div>
            <div className="rowline">
              <button type="button" className="btn primary" disabled={!companion.trim() || !me.trim() || (companion === s.companionName && me === s.userName)} onClick={() => { settingsStore.setNames(companion, me); appStore.notify('改好了。'); }}>保存</button>
              {s.firstMetAt && <span className="hint">{new Date(s.firstMetAt).toLocaleDateString('zh-CN')} 认识的。</span>}
            </div>
          </div>
        </section>

        <section>
          <h2>记忆</h2>
          <div className="card">
            <label className="rowline" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={s.memoryEnabled} onChange={(e) => settingsStore.setMemoryEnabled(e.target.checked)} />
              <span>每聊一轮后让它记笔记（关掉它就只聊天、不学习，用来对照）</span>
            </label>
            <p className="hint">现在有 {app.memories.length} 条笔记、{app.journal.length} 天日记、{app.messages.length} 条消息。<button type="button" className="btn" style={{ marginLeft: 8 }} onClick={() => navigate('/memory')}>去看它记得的</button></p>
          </div>
        </section>

        <section>
          <h2>数据</h2>
          <div className="card">
            <div className="rowline">
              <button type="button" className="btn" onClick={exportAll}>导出备份</button>
              <button type="button" className="btn" onClick={importAll}>导入备份</button>
              <button type="button" className="btn danger" onClick={() => { if (window.confirm('清空全部对话、笔记和日记？Key 和称呼保留。')) void appStore.clearAll().then(() => appStore.notify('清空了。')); }}>清空对话与记忆</button>
            </div>
            <p className="hint">一切只在这台浏览器里：没有服务器，没有账号。清除浏览器站点数据也会清掉这些，换电脑前先导出。</p>
          </div>
        </section>
      </div>
    </div>
  );
}
